import { ChevronRight } from 'lucide-react';
import { Fragment, useEffect, useRef, useState, type ReactNode, useCallback } from 'react';
import { AnchorCellContext } from '../../../../components/table/anchorCell';
import type { ColumnDef } from '../../../../components/index-page/types';
import { Skeleton, SkeletonRows } from '../../../../components/Skeleton';
import { RowActionMenu } from '../../../../components/table/RowActionMenu';
import { useColumnWidths } from '../../../../components/table/useColumnWidths';
import type { OtherInfoData } from '../../../../components/table/OtherInfoModal';
import type { MenuItem } from '../../../../components/Menu';
import {
  STOCK_STATUS,
  withoutId,
  type StockLevelAging,
  type StockLevelGroupFigures,
  type StockLevelGroupRow,
  type StockLevelListItem,
} from '../stockTypes';

/**
 * Stock Levels' own table — the ONE dedicated table surface in the product.
 *
 * **It exists because three things here are shapes `<IndexTable>` refuses**, and refusing them
 * is what keeps every other table in the product one table:
 *
 * 1. **Rows are GROUPED under a collapsible parent that sums its children.** An item name is a
 *    governed vocabulary entry — gloves in three sizes are three catalog items of one name — so
 *    the question "how much glove do we hold" has no row to answer it until the name is summed.
 * 2. **The anchor is a STACKED cell**, brand and model over `unit · SKU`. `<StackedCell secondary>`
 *    is dev-errored inside an index table precisely so this does not spread.
 * 3. **The row menu sits INSIDE the first column**, where the shell draws a pinned trailing
 *    gutter.
 *
 * Everything around it is still the shell's — breadcrumb, folder nav, toolbar, search, facets,
 * filter rail, settings cog, pager, History Logs, Clear data — because this is `renderTable`,
 * an escape hatch, and not a second `<PlantillaIndexShell>`.
 *
 * **Captured from the hospital's `LogisticsStockLevelsGroupedTable`** for its REQUIREMENTS: the
 * name grouping, the summed parent, the banded header. None of its structure came across — the
 * cells, the resize hook, the row menu and the grid's own class strings are rsys_'s, so this
 * reads as the same table as every other one with a different body.
 */

/**
 * The columns pinned to the left edge, in order.
 *
 * They must be the table's LEADING columns and contiguous — `sticky left` positions a cell
 * against the scroll container, so a gap would pin one over the top of a column still moving.
 * The resolver below enforces that by stopping at the first column not named here rather than
 * trusting the declaration order to stay right.
 *
 * A tab that drops one of them simply pins fewer: Real Property has no status dot, so its
 * frozen run is the item alone.
 */
const FROZEN_KEYS = ['name', 'stockDot'];

/*
  THE HEADER AND FOOTER DRAW THEIR HORIZONTAL RULES AS INSET SHADOWS, NOT BORDERS.

  **This table is `border-collapse` and its `<thead>`/`<tfoot>` are `sticky`, and those two
  do not combine.** A collapsed border belongs to the TABLE rather than to the cell, so the
  moment a sticky header actually sticks, the browser promotes it to its own layer and the
  table-painted rules stay behind: the frame and the band separator vanish mid-scroll while
  the vertical rules — redrawn by the cells either side — survive. Reported 12 Oct 2026 with
  two screenshots, at rest and scrolled.

  A shadow is painted by the ELEMENT, so it travels with the promoted layer. `inset 0 1px`
  occupies the cell's own first pixel row rather than the shared edge between two cells,
  which is why these REPLACE the borders instead of joining them — a shadow beside a
  surviving collapsed border reads as one 2px rule at rest and a 1px one once stuck.

  **Do not "restore" these to `border-t`/`border-b`.** It was how they were written and it is
  what produced the defect. The VERTICAL rules stay borders: they paint correctly under
  sticky and the one-rule-per-gap logic above depends on them.
*/
/*
  WRITTEN OUT IN FULL, never composed from colour constants. Tailwind scans the SOURCE for
  literal class strings, so a name built with a template literal — `shadow-[...${FRAME}]` —
  generates no CSS at all: the class reaches the DOM and matches nothing. That is exactly how
  the first attempt at this fix failed, silently, with the markup looking correct in devtools.

  #94a3b8 is slate-400 (the frame) and #cbd5e1 is slate-300 (a band boundary).
*/
const SHADOW_FRAME_TOP = 'shadow-[inset_0_1px_0_0_#94a3b8]';
const SHADOW_FRAME_BOTH = 'shadow-[inset_0_1px_0_0_#94a3b8,inset_0_-1px_0_0_#94a3b8]';
const SHADOW_BAND_TOP_FRAME_BOTTOM = 'shadow-[inset_0_1px_0_0_#cbd5e1,inset_0_-1px_0_0_#94a3b8]';

export function StockLevelsIndexTable({
  module,
  columns,
  rows,
  rowKey,
  loading,
  sort,
  onToggleSort,
  onRowOpen,
  rowActions,
  otherInfo,
  bands,
  groupActions,
  refreshingTypeIds = null,
  queryKey,
  expandGroups,
  reloadKey,
  fetchFigures,
  fetchChildren,
  fetchAging,
  agingColumns,
}: {
  module: string;
  columns: ColumnDef<StockLevelListItem>[];
  rows: StockLevelListItem[];
  rowKey: (row: StockLevelListItem) => string;
  loading: boolean;
  sort: string | null;
  onToggleSort: (field: string) => void;
  /**
   * Opens the row. The second argument says whether its AGING figures are real.
   *
   * <b>A row handed over while the aging call is in flight carries ZEROS in those ten fields</b>
   * — `Project` leaves them at `default` and `WithAging` is what fills them. The cells in this
   * table draw a skeleton for that window and correct themselves when the call lands; a surface
   * the row is handed TO has neither, so it would hold the zeros for as long as it is open. The
   * flag is what lets it say "not in yet" instead of "none expired".
   */
  onRowOpen?: (row: StockLevelListItem, agingKnown: boolean) => void;
  rowActions?: (row: StockLevelListItem, agingKnown: boolean) => MenuItem[];
  otherInfo: (row: StockLevelListItem) => OtherInfoData | null;
  /**
   * Header bands, in column order — a run of `ColumnDef.key`s under one heading.
   *
   * <b>`emphasis` boxes ONE band a step above the others.</b> Declared on the band rather than
   * matched by label in here, for the reason the band and its columns are one declaration: a
   * label is the reader's word and is free to change, where what is being said is that THIS
   * group is the one the table is opened for.
   */
  bands: { label: string; keys: string[]; emphasis?: boolean }[];
  /**
   * The GROUP row's menu — actions on the item TYPE rather than on any one item.
   *
   * Its entries address another module (`inventory-item-types`), so each carries that
   * module's own key rather than this page's: the key of what an action DOES, never the key
   * of the page it sits on. `<Menu>` hides the ones the reader lacks, and a group left with
   * none draws no kebab at all.
   */
  groupActions?: (group: { id: string; name: string; items: StockLevelListItem[] }) => MenuItem[];
  /**
   * A newer copy of the SAME query is in flight. Accepted from the shell and DELIBERATELY
   * UNREAD, because per-group fetching answers it more precisely.
   *
   * A refresh here is not one thing arriving: it is the groups a write moved refetching their
   * totals while every other row stays exactly as it is. `refreshingTypeIds` is what says
   * which, and the skeleton follows from those rows’ own figures going back in flight.
   */
  refreshing?: boolean;
  /**
   * WHICH item TYPES the write was against, so the reload is scoped to those groups.
   *
   * Every figure on this page is derived, so issuing one laptop moves that item’s row, its
   * group’s totals and the tab counts — and nothing else. Skeletoning the whole table to
   * report that throws away the tab, the scroll and every row the reader was reading.
   *
   * <b>The TYPE rather than the item</b>, and that is the change progressive loading forced:
   * a group is what holds figures now, so naming the item would leave the table resolving it
   * against children it may not have fetched.
   *
   * <b>A SET rather than one id</b>, and that is what an issuance forced: a slip carries lines
   * across several types, so a scope naming one of them leaves the rest showing figures that
   * have just moved. A stale row under a correct one is worse than reloading the table, because
   * nothing on screen says which of the two it is.
   *
   * Null reloads every group, which is the honest answer for a write whose reach the page
   * cannot name — a type retired, an item registered into a group that may not exist yet.
   */
  refreshingTypeIds?: readonly string[] | null;
  /**
   * The FILTERS every per-group fetch is scoped to, as one string.
   *
   * Read by every group, always. A filter change is a different question, so no group may
   * carry on answering the previous one — which is the half that must NOT be scoped.
   */
  queryKey: string;
  /**
   * Open every group on arrival, rather than leaving them collapsed.
   *
   * <b>It is ON while the reader is SEARCHING.</b> A search has already narrowed the groups to
   * the ones holding a match, so the question "which of these is it" is the only one left — and
   * a collapsed row answers it with a count the reader then has to click. Typing a SKU and
   * meeting a shut folder is the search working and not looking like it.
   *
   * Collapsed is still the resting state, which is the hospital's call: the page is opened to
   * find what to reorder, and the summed row answers that.
   */
  expandGroups: boolean;
  /**
   * Bumped by every write, and the ONE thing `refreshingTypeId` scopes.
   *
   * It is deliberately separate from `queryKey` rather than folded into it. One key carrying
   * both meant a group that ignored a save went on to ignore the next FILTER change as well,
   * because its pinned value had stopped tracking either.
   */
  reloadKey: number;
  /**
   * ONE group’s summed totals. `null` where the filters leave the group empty — a 404, which
   * the row reads as "no figures" rather than as a row of zeros.
   */
  fetchFigures: (typeId: string, signal: AbortSignal) => Promise<StockLevelGroupFigures | null>;
  /** ONE group’s children, fetched the first time the reader expands it and not before. */
  fetchChildren: (typeId: string, signal: AbortSignal) => Promise<StockLevelListItem[]>;

  /** The fifth call — the figures that age, fetched apart so the rows appear first. */
  fetchAging: (typeId: string, signal: AbortSignal) => Promise<StockLevelAging[]>;

  /**
   * WHICH columns read an aging figure, by `ColumnDef.key` — the cells that draw a skeleton
   * until `fetchAging` lands rather than the zero the stored row carries.
   *
   * It is the PAGE's to declare, beside the renders it describes: a column key is not a DTO
   * field name, so nothing in here can derive it. See `AGING_COLUMN_KEYS`.
   */
  agingColumns: ReadonlySet<string>;
}) {
  const { widths, startResize } = useColumnWidths(`${module}.grouped`, columns);

  /*
    EVERY GROUP'S FIGURES, for the page total in the footer.

    They live in the GroupRow that fetched them, so the footer has to be told — `onFigures` is
    the one way up. Held as a record keyed by item type rather than an array, so a group
    reporting twice replaces its own entry instead of adding a second.

    IT IS NOT CLEARED HERE on a filter change, and that is deliberate: every group refetches on
    the same `queryKey` and overwrites its own entry, so clearing first would blank the footer
    for the length of the slowest fetch rather than letting it settle. What DOES drop out is a
    group that is no longer on the page — `pageTotalRow` sums the rows it is given, so a stale
    entry for a type the filters removed is never read.
  */
  const [figuresByType, setFiguresByType] = useState<
    Record<string, StockLevelGroupFigures | null>
  >({});

  /*
    STABLE, because it is a dependency of every group's figures effect. An inline arrow would
    be a new function on every render of this table, which would re-run that effect — and each
    run refetches, so the groups would never stop loading.
  */
  const collectFigures = useCallback(
    (typeId: string, figures: StockLevelGroupFigures | null) =>
      setFiguresByType((current) => ({ ...current, [typeId]: figures })),
    [],
  );

  /** The footer's row. Null until at least one group has answered. */
  const total = pageTotalRow(rows, figuresByType);

  /*
    NO CLIENT-SIDE GROUPING LEFT, and no expanded-set either.

    `groupByName` bucketed a page of ITEMS under their type name, which was the only shape
    available while one call returned every row with every figure on it. `rows` is now one per
    TYPE already — the server groups — so each row owns its own open state, its own figures and
    its own children, and a group can load without any of the others.
  */

  /*
    ONE BAR, the width `<SkeletonRows>` uses — two thirds of the cell, capped, so a column of
    them reads as a column rather than as a block. An ARBITRARY max-width because nothing else
    in this product uses the numeric max-width scale, and a class Tailwind does not emit fails
    by leaving the bar unbounded, silently.
  */
  const staleBar = <Skeleton className="h-4 w-2/3 max-w-[96px]" />;

  /*
    THE HEADER RUNS, computed ONCE and read by both header rows and by every body cell.

    It used to be derived twice — `buildHeaderRuns` for the band row and a `bandOf` filter for
    the column row — which is two answers to "does this column sit under a band". They agreed
    while every band held several columns and stopped agreeing the moment one held one: the
    band row drew it as a tall spanning cell AND the column row drew its header again
    underneath, printing the same word twice, one line above itself.
  */
  const runs = buildHeaderRuns(columns, bands);
  const bandRuns = runs.filter((run) => run.kind === 'band');

  /** The columns that genuinely sit UNDER a band — the second header row draws these and no others. */
  const banded = new Set(bandRuns.flatMap((run) => run.keys));

  /*
    THE GROUP'S OUTER EDGES. A band is a claim that these columns are read together, and a
    heading with nothing bounding it is a word floating over a run of columns that look exactly
    like the run beside them — which is what the header was before 21 Sep 2026.

    Drawn as a DARKER rule at the first and last column of each band, running the full height of
    the table rather than stopping at the header: the grouping is a property of the columns, not
    of their labels, so a boundary that ended under the heading would have to be re-found by eye
    on every row. That is the tree chart's line-tone rule reaching a table — `slate-300` for the
    boundary against `slate-200` for the rules inside it, so a group reads as one step forward
    of its own columns.

    <b>THE STEP IS THE RULE; THE STEPS ARE NOT.</b> All of them moved UP one on 21 Sep 2026,
    because the grid was very nearly invisible — `slate-100` is about 1.1:1 on white, so a
    twenty-five column table read as figures floating in space — and all of them moved back DOWN
    one on 22 Sep, because at 400 the boundary stopped bounding a group and started dividing the
    table into regions.

    <b>Whichever way they go, they go TOGETHER.</b> That is the tree chart's own rule and the
    half that matters: moving one alone is what flattens a hierarchy. Lightening the boundary by
    itself would have left it equal to the header rules it is supposed to step above — so the
    band would have lost its edge in the header row entirely, which is a functional loss dressed
    up as a tweak.
  */
  const bandStart = new Set(bandRuns.map((run) => run.keys[0]!));
  const bandEnd = new Set(bandRuns.map((run) => run.keys[run.keys.length - 1]!));

  /*
    The LEFT rule's tone per column, resolved rather than layered.

    Emitting `border-slate-100` and `border-l-slate-300` together would leave two declarations
    of one `border-left-color` at equal specificity, and which of them wins is Tailwind's
    stylesheet order rather than anything this file states — a border that is correct today and
    silently the wrong tone after a utility is added elsewhere.
  */
  /*
    EVERY BOUNDARY IS DRAWN ONCE, AS A LEFT RULE. Fixed 22 Sep 2026.

    It was drawn twice. A band's last column emitted `border-r-slate-400` and the next band's
    first column emitted `border-l-slate-400`, and since the bands run contiguously from
    Investment to Stock status that put TWO 1px rules in every gap between them — a visibly
    heavier line after Available, Dispensed, Quarantined and the rest, against the single rule
    everywhere else. `border-collapse` does not merge them, because they belong to different
    cells and both are declared.

    So the closing rule is now drawn only where nothing follows to draw its own left — see
    `rightEdge` — and everything else is one column's left border.
  */
  const boundary = new Set<string>(bandStart);

  /*
    THE BOXED BAND, a step above every other boundary. Product call, 29 Sep 2026.

    Two keys, and they are the band's two vertical edges read the way this table already draws
    every edge: the band's FIRST column carries its left rule, and the column AFTER its last
    carries the right one — because a boundary is drawn ONCE, as a left rule, and emitting both
    sides is what put 2px in every gap before 22 Sep 2026.

    Where the band ENDS the table there is no following column to draw it, so the closing rule
    is the band's own — see `rightEdge`, which is the one place a right border is legitimate.

    <b>slate-400 against the slate-300 every other band draws.</b> It is one step, not a black
    box: the figures are the content and a rule heavier than they are would frame the table
    rather than the group. And it only reads as emphasis BECAUSE the others stayed at 300 —
    moving them together is what flattened it when all nine were tried at 400.
  */
  const strong = new Set<string>();

  for (const run of bandRuns) {
    if (!run.emphasis) continue;

    strong.add(run.keys[0]!);

    const lastOfBand = run.keys[run.keys.length - 1]!;
    const after = columns[columns.findIndex((column) => column.key === lastOfBand) + 1];

    if (after) strong.add(after.key);
  }

  /*
    THE DOT COLUMN IS A BOUNDARY TOO, on both sides. Product call, 22 Sep 2026.

    It is chrome rather than a value — a status dot with no header — sitting between the row's
    identity and the twenty-odd figures. At the light tone it read as one more narrow column of
    data, so the eye had nothing separating the thing being named from the numbers about it.

    Its RIGHT side is the next column's left, which is the same rule as everywhere else: what is
    added here is the dot's own left, and the left of whatever follows it.
  */
  const dotIndex = columns.findIndex((column) => column.key === 'stockDot');

  if (dotIndex !== -1) {
    boundary.add('stockDot');

    const after = columns[dotIndex + 1];
    if (after) boundary.add(after.key);
  }

  const leftTone = (key: string, inside: string) =>
    strong.has(key) ? 'border-l-slate-400' : boundary.has(key) ? 'border-l-slate-300' : inside;

  /*
    The CLOSING rule, and it is the table's own outer edge rather than a band's.

    Only the last column draws one: every other gap is already the next cell's left border, and
    drawing both is what doubled every boundary before 22 Sep 2026. `bandEnd` is still read,
    because a trailing column belonging to no band has no grid line to close.
  */
  const lastKey = columns[columns.length - 1]?.key;

  /*
    A BOXED band that ends the table closes itself, because nothing follows to draw its left —
    which is the only case a right border is drawn here, and the same case the plain closing
    rule already covers.
  */
  const strongEnd = new Set(bandRuns.filter((run) => run.emphasis).map((run) => run.keys[run.keys.length - 1]!));

  const rightEdge = (key: string) =>
    key === lastKey && strongEnd.has(key)
      ? 'border-r border-r-slate-400'
      : key === lastKey && bandEnd.has(key)
        ? 'border-r border-r-slate-300'
        : '';

  /*
    THE HEADER CELL'S CONTENTS — the label, its sort control where the column has one, and the
    resize grip — written ONCE and rendered by both branches of the band row.

    <b>It was only in the second row, and that was a real defect rather than a tidy-up.</b> A
    column belonging to no band is drawn as a tall cell in the FIRST row, and that branch
    rendered nothing but the header text — so `Item`, `Near expiry`, `Latest movement` and
    `Stock status` could be neither sorted nor resized, with `latestMovementAt` declaring
    `sortable: true` and offering no control to sort by. The standard table contract is that
    every column the server can order by carries a header that sorts and a grip that resizes;
    this surface is dedicated, not exempt.

    It surfaced when Investment gained a group of its own and therefore stopped being banded —
    the move would have silently taken its sort and its resize with it.
  */
  const headerBody = (column: ColumnDef<StockLevelListItem>) => {
    const isSorted = sort === column.key || sort === `-${column.key}`;

    return (
      <>
        {column.sortable ? (
          <button
            type="button"
            id={`sort-${column.key}`}
            onClick={() => onToggleSort(column.key)}
            aria-sort={isSorted ? (sort?.startsWith('-') ? 'descending' : 'ascending') : 'none'}
            className={[
              'flex w-full items-center gap-1 uppercase tracking-wider transition hover:text-slate-700',
              column.align === 'right' ? 'justify-end' : 'justify-between',
            ].join(' ')}
          >
            <span className="min-w-0">{column.header}</span>
          </button>
        ) : (
          <span className={column.align === 'right' ? 'block text-right' : undefined}>
            {column.header}
          </span>
        )}

        <span
          role="separator"
          aria-orientation="vertical"
          aria-label={`Resize ${column.header}`}
          onPointerDown={(e) => startResize(columns.indexOf(column), e)}
          className="absolute -right-0.5 top-0 z-10 h-full w-1.5 cursor-col-resize hover:bg-blue-300"
        />
      </>
    );
  };

  /*
    Exactly as wide as its columns add up to, with `minWidth: 100%` stretching a narrow table
    to fill the panel — the documented width rule, not `w-full`. At twenty-five columns
    `width: 100%` would squeeze every column below the width its content needs, so nothing
    would ever overflow and neither the ellipsis nor the wrap rule could fire.
  */
  const totalWidth = columns.reduce(
    (sum, column, index) => sum + (widths[index] ?? column.width),
    0,
  );

  /** What each column asks for before anybody drags it — see the `<colgroup>`. */
  const defaults = columns.map((column) => column.width);

  /*
    THE FROZEN PAIR — the item and its status dot, pinned to the left edge. 22 Sep 2026.

    This table is twenty-five columns wide, so a reader comparing a figure on the right has
    scrolled the row's own identity off the screen and is reading numbers with nothing saying
    whose they are. That is exactly the case `<IndexTable>`'s `freezeAnchor` exists for, and
    this dedicated surface owes it for the same reason.

    <b>The DOT is frozen with it rather than instead of it.</b> It is the one column a reader
    scans down rather than reads, so pinning the name and leaving the state to scroll away
    would keep the label and lose the thing the label is being checked against.

    <b>Keyed, never indexed.</b> The column SET differs per tab — Real Property drops the dot
    entirely — so a positional rule would pin whatever inherited the index. The loop breaks at
    the first column that is not frozen, because `sticky left` only works on a leading,
    contiguous run: a gap would pin a column over the top of one still scrolling.
  */
  const frozenLefts = new Map<string, number>();

  {
    let offset = 0;

    for (const [index, column] of columns.entries()) {
      if (!FROZEN_KEYS.includes(column.key)) break;

      frozenLefts.set(column.key, offset);
      offset += widths[index] ?? column.width;
    }
  }

  /*
    The classes a frozen cell needs beyond its position, and the BACKGROUND is the load-bearing
    half: a sticky cell with a transparent ground lets the columns it is pinned over scroll
    through it. It has to match the row it sits in, hover included — hence `group-hover`, which
    every row in this table already declares `group` for.
  */
  const frozenCell = (key: string, ground: string) =>
    frozenLefts.has(key) ? `sticky ${ground}` : '';

  /*
    A DATA CELL’S CHROME, resolved once and handed to `<GroupRow>`.

    The band boundaries, the frozen offsets and the numeric alignment are all computed up here
    from the resolved columns, and a row rendered in a child component has no way to reach
    them. Passing two functions rather than six values is what keeps the group row from having
    to re-derive a grid it does not own — and re-deriving it is how the skeleton came to draw
    no vertical rules while the real rows drew them all.
  */
  const cellClass = (column: ColumnDef<StockLevelListItem>) =>
    [
      'border-l px-3 py-2 align-middle first:border-l-0',
      leftTone(column.key, 'border-l-slate-200'),
      rightEdge(column.key),
      column.align === 'right' ? 'text-right' : 'text-left',
      frozenCell(column.key, 'z-10 bg-white group-hover:bg-slate-50'),
    ].join(' ');

  const cellStyle = (column: ColumnDef<StockLevelListItem>) => ({
    left: frozenLefts.get(column.key),
  });

  return (
    /*
      NO scroll wrapper of its own — the shell already provides one, and `<IndexTable>` returns
      a bare table for exactly this reason. A second `overflow-auto` here nested one scroll
      region inside another AND left the table with no definite height to resolve `height: 100%`
      against, so the spacer row claimed nothing and the column rules stopped at the last row.
    */
    <table
      className="border-collapse text-left text-xs"
      style={{ tableLayout: 'fixed', width: totalWidth, minWidth: '100%', height: '100%' }}
    >
      {/*
        THE SLACK GOES TO ONE COLUMN, and that is the whole of this fix. 22 Sep 2026.

        Every `<col>` carried an explicit width, so whenever the columns added up to LESS than
        the panel `minWidth: 100%` stretched the table and the browser distributed the surplus
        PROPORTIONALLY across all of them. Dragging one column narrower therefore widened every
        other column on screen — a drag that visibly fought back, which is the one thing the
        no-compensation rule exists to prevent.

        It was invisible while this table ran to twenty-five columns, because the total was
        always wider than the panel. The Headers cog is what surfaced it: put three groups away
        and the remaining columns no longer fill the panel.

        So the soft-flex anchor stays WIDTH-LESS until it is dragged, exactly as `<IndexTable>`
        does it — the browser hands the slack to the one column with no width to honour, which
        is the behaviour a flex column was always meant to have.

        <b>The residual, stated rather than discovered:</b> once the anchor itself is dragged it
        takes an explicit width and there is nothing left to absorb slack, so the distribution
        returns. That is the same trade `<IndexTable>` makes, and closing it properly means a
        trailing spacer column in all four row branches rather than a different rule here.
      */}
      <colgroup>
        {columns.map((column, index) => (
          <col
            key={column.key}
            style={
              column.flex && widths[index] === defaults[index]
                ? undefined
                : { width: widths[index] ?? column.width }
            }
          />
        ))}
      </colgroup>

        <thead className="sticky top-0 z-20">
          {/*
            The BAND row. A run of columns under one heading, and every column belonging to no
            band spans both rows instead — so an identity column is one tall cell rather than a
            cell under an empty one, which is what makes the bands read as bands.
          */}
          {/*
            THE HEADER BLOCK IS FRAMED, top and bottom, a step above every rule inside it —
            product call, 02 Oct 2026, in the reader's own words: *make the table header top,
            bottom border darker as well*.

            `slate-400` is the tone the boxed Summary band already draws at, so the header reads
            as framed at the strongest weight on the table rather than introducing a fourth. The
            grid INSIDE it stays `slate-200` and a band boundary stays `slate-300`: the step is
            what makes the frame read, so darkening those with it would flatten it again.

            The table is `border-collapse`, so the bottom edge here and the first body row's own
            `border-t` are ONE line. It is declared on the header because that is the side that
            must win, and it was MEASURED on the rendered page rather than read off the class.
          */}
          <tr className="bg-slate-100 text-[9px] uppercase tracking-wider text-gray-600 [&>th]:font-normal">
            {runs.map((run) =>
              run.kind === 'column' ? (
                <th
                  key={`span-${run.key}`}
                  rowSpan={2}
                  scope="col"
                  /*
                    `leftTone` rather than a fixed slate-300: an unbanded column can be a
                    BOUNDARY too — the dot column is — and hard-coding the tone here is what
                    left its rule light while the body rows drew it dark.
                  */
                  className={[
                    'relative border-l px-3 py-2 align-bottom first:border-l-0',

                    // It spans the band row AND the sub-header row, so it is the only cell in
                    // its column carrying both halves of the frame.
                    SHADOW_FRAME_BOTH,
                    leftTone(run.key, 'border-l-slate-200'),
                    rightEdge(run.key),

                    // z-40 over the header's own z-20, so a frozen heading passes OVER the
                    // headings scrolling beneath it rather than under them.
                    frozenCell(run.key, 'z-40 bg-slate-100'),
                  ].join(' ')}
                  style={{ left: frozenLefts.get(run.key) }}
                >
                  {headerBody(columnOf(columns, run.key))}
                </th>
              ) : (
                <th
                  key={`band-${run.label}`}
                  colSpan={run.keys.length}
                  scope="colgroup"
                  /*
                    The SAME one-rule-per-gap rule as the cells below it. A band cell spans its
                    whole run, so its left IS the boundary and its right is the next band's
                    left — emitting both put two rules in every gap on this row exactly as it
                    did on the others.
                  */
                  className={[
                    'border-l px-3 py-2 text-center first:border-l-0',

                    // TOP only — its bottom is the band separator, drawn by the sub-header
                    // cells beneath it so it spans exactly the columns the band covers.
                    SHADOW_FRAME_TOP,

                    /*
                      `leftTone` rather than a fixed slate-300, for the reason the branch above
                      states and this one used to ignore: a band may be BOXED, and hard-coding
                      the tone here drew its edge light across the heading and dark down every
                      row beneath it — one line in two colours, which reads as a rendering fault
                      rather than as emphasis. Measured on the page, not inferred.
                    */
                    leftTone(run.keys[0]!, 'border-l-slate-300'),
                    rightEdge(run.keys[run.keys.length - 1]!),
                  ].join(' ')}
                  /* A BAND is never frozen — the frozen run is the leading identity columns,
                     which belong to no band. Stated so the omission does not read as missed. */
                >
                  {run.label}
                </th>
              ),
            )}
          </tr>

          <tr className="bg-slate-100 text-[9px] uppercase tracking-wider text-gray-600 [&>th]:font-normal">
            {columns
              .filter((column) => banded.has(column.key))
              .map((column) => {
                return (
                  <th
                    key={column.key}
                    scope="col"
                    /*
                      The TOP rule is what attaches these headers to the band above them. It is
                      drawn on the CHILD rather than as a bottom border on the band cell so it
                      spans exactly the columns the band covers — which is the same span today
                      and stays the same span if a band ever sits beside an unbanded column.
                    */
                    className={[
                      'relative border-l px-3 py-2 align-middle',

                      // The band separator above it and the frame's bottom below it, in one
                      // declaration — see SHADOW_* for why neither is a border any more.
                      SHADOW_BAND_TOP_FRAME_BOTTOM,
                      leftTone(column.key, 'border-l-slate-200'),
                      rightEdge(column.key),
                    ].join(' ')}
                  >
                    {headerBody(column)}
                  </th>
                );
              })}
          </tr>
        </thead>

        <tbody>
          {/*
            SKELETON while the first answer is in flight — the same component every other table
            in the product uses, so a loading list looks like a loading list wherever it is.

            Groups arrive COLLAPSED, so six placeholder rows is the shape the real answer takes:
            one per item type, not one per item. A skeleton that promised twenty rows and
            resolved to six would resettle the whole panel the moment the data landed.
          */}
          {loading ? (
            /*
              It is handed the SAME cell chrome the real rows carry — the column rules, the
              band boundaries, the two frozen offsets and the numeric alignment — because a
              skeleton is the shape of what it replaces. Without it this table drew no
              vertical rules at all while loading and grew them the instant data landed.

              `frozenCell` takes `bg-white` here rather than the row ground: a placeholder row
              has no hover and no group tint, and a pinned cell still has to be opaque or the
              columns it is pinned over scroll through it.
            */
            <SkeletonRows
              rows={6}
              columns={columns.length}
              cell={(index) => {
                const column = columns[index]!;

                return {
                  className: [
                    'border-l first:border-l-0',
                    leftTone(column.key, 'border-l-slate-200'),
                    rightEdge(column.key),
                    column.align === 'right' ? 'text-right' : 'text-left',
                    frozenCell(column.key, 'z-10 bg-white'),
                  ].join(' '),
                  style: { left: frozenLefts.get(column.key) },
                };
              }}
            />
          ) : (
            /*
              ONE ROW PER ITEM TYPE, straight from the server — no client-side grouping left.

              `groupByName` used to build these by walking a page of ITEMS and bucketing them,
              which meant every figure under every group had already been computed whether or
              not anybody expanded one. The table opens COLLAPSED, so that was most of the work
              on the page thrown away before it was drawn.

              Each row now fetches its OWN totals and its OWN children. A group holding four
              hundred items no longer holds up the nine beside it.
            */
            rows.map((group) => (
              <GroupRow
                key={group.id}
                module={module}
                columns={columns}
                group={group}
                cellClass={cellClass}
                cellStyle={cellStyle}
                staleBar={staleBar}
                queryKey={queryKey}
                expandGroups={expandGroups}
                onFigures={collectFigures}
                reloadKey={reloadKey}
                refreshingTypeIds={refreshingTypeIds}
                fetchFigures={fetchFigures}
                fetchChildren={fetchChildren}
                fetchAging={fetchAging}
                agingColumns={agingColumns}
                rowKey={rowKey}
                onRowOpen={onRowOpen}
                rowActions={rowActions}
                otherInfo={otherInfo}
                groupActions={groupActions}
              />
            ))
          )}

          {!loading && rows.length === 0 ? (
            <tr>
              <td colSpan={columns.length} className="px-3 py-10 text-center text-slate-500">
                No items yet. Stock levels are derived from the item catalog.
              </td>
            </tr>
          ) : null}
          {/*
            THE SPACER, and it is what carries every column's rule to the bottom edge.

            A table sized to its rows stops its vertical dividers at the last one, leaving the
            grid hanging in the middle of the panel — and this table is usually short, because
            the groups arrive collapsed. So the table is stretched and this row absorbs the
            slack: `height: 100%` on a table hands its surplus to the ROW boxes, distributed
            across them, which is what once rendered a two-row list as two 320px rows.

            **One `<td>` PER COLUMN, never one `colSpan` cell.** The whole point is the
            `border-l` on each: a single spanning cell stretches just as well and draws no
            dividers, which is the version that looks right until you notice the grid stops.

            **And that includes the BAND BOUNDARY**, which is the half this row was missed for
            on 21 Sep 2026. The group's darker rule was drawn on the header and on the data
            cells and not here — so on a table whose groups arrive COLLAPSED, and which is
            therefore mostly spacer, the boundary stopped at the last row and every ordinary
            column rule carried on past it. The band read as ending halfway down the panel.
          */}
          {/*
            <b>`border-t` like every data row</b>, which is what CLOSES the last one. Added
            22 Sep 2026: the rows draw their rule on the TOP, so the final row had nothing under
            it and its figures ran into the empty space below — the one row in the table with an
            open bottom edge.

            It belongs on the spacer rather than as a `last:border-b` on the data rows, because
            the spacer is always the last row: a bottom border on the data row would draw a
            second line wherever the spacer already sits under it.
          */}
          <tr aria-hidden="true" className="h-full border-t border-slate-200">
            {columns.map((column) => (
              <td
                key={column.key}
                className={[
                  'border-l p-0 first:border-l-0',
                  leftTone(column.key, 'border-l-slate-200'),
                  rightEdge(column.key),
                ].join(' ')}
              />
            ))}
          </tr>
        </tbody>

        {/*
          THE PAGE TOTAL, pinned to the bottom edge.

          <b>Sticky, unconditionally</b>, which is the product's own rule for a totals footer:
          a total is what the rows add up to, so a scroll that carried it off the bottom would
          hide the one figure a grid of figures is opened for. The table is already stretched
          and the spacer above already absorbs the slack, so the pin has something to pin
          against on a short table too.

          <b>NOT BOLD.</b> The tint and the frame are the treatment — a footer that also carried
          the heaviest weight on a surface whose whole content is numbers would compete with the
          figures it totals.

          <b>`z-20`, not the panel's `z-10`</b>: this table pins its own gutter cells at
          `z-30` and its header at `z-20`, so the footer has to clear the body it floats over.

          It renders only once something has ARRIVED. A footer of em dashes under a table still
          loading says nothing, and a row of zeros would say something false.
        */}
        {total ? (
          <tfoot className="sticky bottom-0 z-20">
            <tr className="bg-slate-100 text-slate-600">
              {columns.map((column) => (
                <td
                  key={column.key}
                  className={[
                    cellClass(column),
                    'bg-slate-100',

                    // Sticky at the BOTTOM, so its top rule is the one that disappears.
                    SHADOW_FRAME_TOP,
                  ].join(' ')}
                  style={cellStyle(column)}
                >
                  {column.key === 'name' ? (
                    <span className="text-[9px] uppercase tracking-wider text-gray-600">
                      Total · {rows.length === 1 ? '1 group' : `${rows.length} groups`}
                    </span>
                  ) : FOOTER_REFUSES.has(column.key) ? null : (
                    column.render(total, 0)
                  )}
                </td>
              ))}
            </tr>
          </tfoot>
        ) : null}
    </table>
  );
}

/** One band's label for a column, or null where the column belongs to none. */
function bandOf(bands: { label: string; keys: string[] }[], key: string): string | null {
  return bands.find((band) => band.keys.includes(key))?.label ?? null;
}

/**
 * The column a header run names.
 *
 * It returned the header STRING (`headerOf`) while the band row drew an unbanded column as
 * bare text; that row renders the same sortable, resizable cell body as the second row now,
 * so it needs the column rather than one field of it. A run is built by walking `columns`, so
 * the lookup cannot miss — the assertion states that rather than inviting a `?? null` every
 * caller would have to branch on.
 */
function columnOf(columns: ColumnDef<StockLevelListItem>[], key: string) {
  return columns.find((column) => column.key === key)!;
}

/**
 * The header's top row, as a run per band and a cell per ungrouped column.
 *
 * **Walks the COLUMNS, not the bands** — so a band naming a key the table does not have cannot
 * shift every heading one column left, which is the failure mode that is invisible until
 * somebody screenshots it.
 *
 * An ungrouped column gets its OWN `rowSpan={2}` cell rather than sharing one with its
 * neighbours: they are unrelated columns that happen to sit together, and one cell spanning
 * four of them would read as an unnamed band.
 */
function buildHeaderRuns(
  columns: ColumnDef<StockLevelListItem>[],
  bands: { label: string; keys: string[]; emphasis?: boolean }[],
): ({ kind: 'column'; key: string } | { kind: 'band'; label: string; keys: string[]; emphasis: boolean })[] {
  const runs: ({ kind: 'column'; key: string } | { kind: 'band'; label: string; keys: string[]; emphasis: boolean })[] = [];

  for (const column of columns) {
    const label = bandOf(bands, column.key);

    if (label === null) {
      runs.push({ kind: 'column', key: column.key });
      continue;
    }

    const last = runs[runs.length - 1];

    if (last && last.kind === 'band' && last.label === label) last.keys.push(column.key);
    else {
      runs.push({
        kind: 'band',
        label,
        keys: [column.key],

        // Read off the DECLARATION, so a band reduced to one column by a tab's `columnKeys`
        // carries no stale emphasis when it collapses into a plain header below.
        emphasis: bands.find((band) => band.label === label)?.emphasis === true,
      });
    }
  }

  /*
    A BAND OF ONE COLUMN IS NOT A BAND, and it collapses into the tall spanning cell an
    unbanded column already gets.

    Two ways a run ends up one wide, and the treatment is right for both. A band DECLARED over
    one column exists so the settings cog can offer that column its own tick — Investment, Near
    expiry, Latest movement, Stock status — and a heading over its own heading prints the same
    word twice, one line above itself. And a MULTI-column band can be reduced to one by a tab's
    own `columnKeys`, where a label naming a band the reader can no longer see the rest of is
    worse than naming the column that survived.

    So the cog's list and the header's bands stay ONE declaration — which is what stops a column
    being offered a tick it has no group for — without that declaration forcing a band onto the
    header for every column somebody wants to be able to hide.
  */
  return runs.map((run) =>
    run.kind === 'band' && run.keys.length === 1
      ? ({ kind: 'column', key: run.keys[0]! } as const)
      : run,
  );
}


/**
 * A GROUP as the table can hold it BEFORE its figures exist.
 *
 * `GET /api/stock-levels/groups` answers with a type, a name and a count — which is the whole
 * point of that call, and why it returns in under 100 ms against a projection that takes two
 * and a half seconds. The table’s columns render a `StockLevelListItem`, so the row has to be
 * one; every numeric field below is a zero the TYPE requires rather than a total anybody
 * computed.
 *
 * <b>NONE OF THOSE ZEROS IS EVER RENDERED, and one branch is what guarantees it.</b>
 * `<GroupRow>` draws a skeleton for every cell but the first until its own figures land, and
 * the row the columns are finally handed comes from `levelFromGroupFigures`. A column render is
 * never called with a pending row — which is the same promise `isGroupTotal` makes about the
 * thresholds, kept the same way: by the renderer branching, not by the value being plausible.
 *
 * That is deliberately NOT the shape the old client-side path had. `aggregate` spread its FIRST
 * CHILD and overrode what it summed, so every column nobody remembered to override was silently
 * one item’s value presented as the group’s — a row that read correctly and meant something
 * else. Here there is no child to borrow from at all.
 */
export function pendingGroupRow(group: StockLevelGroupRow): StockLevelListItem {
  return {
    // `group:` so a placeholder can never collide with a child’s own id in `rowKey`.
    id: `group:${group.groupId}`,

    /*
      THE GROUP'S OWN ID IN THE ITEM-TYPE SLOT, and on the Real Property tab it is a CATEGORY
      id — the row is a placeholder satisfying `StockLevelListItem`, and `<GroupRow>` reads
      the id off this field to scope its three fetches. Nothing renders it: the id is the
      group's, which is what the three calls are scoped by, and the axis travels on the query
      beside it.
    */
    inventoryItemTypeId: group.groupId,
    name: group.name,

    // The two markers. Together they say: this is a TOTAL, and the total is not in yet.
    isGroupTotal: true,
    figuresPending: true,
    groupItemCount: group.itemCount,

    /*
      EVERYTHING BELOW IS A PLACEHOLDER. Read the note above before reaching for one of these
      values — a group has no SKU, no unit, no kind and no reorder level, and the zeros are the
      row type being satisfied rather than anything being claimed.
    */
    sku: '',
    unitCode: '',
    inventoryCategoryId: '',
    inventoryCategoryName: '',
    kind: 0 as StockLevelListItem['kind'],
    manufacturer: null,
    brandName: null,
    model: null,
    licensingModel: null,
    tracksSerial: false,

    investment: null,
    registeredUnits: 0,
    onHand: 0,
    available: 0,
    unusable: 0,
    writtenOff: 0,

    onLoan: 0,
    assigned: 0,
    delayedReturn: 0,
    dispensed: 0,
    forInspection: 0,

    expired: 0,
    damaged: 0,
    underRepair: 0,
    quarantined: 0,

    condemned: 0,
    stolen: 0,

    nearExpiry: 0,
    nearExpiryDays: 0,

    leaseExpiring: 0,
    leaseExpired: 0,
    earliestLeaseEnd: null,
    leaseEndingDays: 0,

    registeredProperties: 0,
    leased: 0,

    reorderLevel: 0,
    maxStockLevel: null,
    stockStatus: STOCK_STATUS.adequate,

    latestMovementAt: null,
  };
}

/**
 * A GROUP’S SUMMED FIGURES as a synthetic row, so the columns render them unchanged.
 *
 * This is `aggregate` moved to the server and read back. That one walked a page of children
 * and added them up in the browser — correct, and only possible because every child was
 * already on screen. With the children arriving on expand there is nothing here to add up, so
 * the sums come from `GET /api/stock-levels/groups/{id}/figures` instead.
 *
 * <b>What it does NOT carry is the contract, exactly as before.</b> No reorder level, no
 * maximum, no near-expiry window — a THRESHOLD is per item, and two of them added together
 * produce a number nothing reorders against. `isGroupTotal` is what makes those columns render
 * an em dash, and it is the same flag the client-side version set.
 *
 * <b>The zeros it writes into the threshold fields are never read</b>, because `perItem`
 * columns branch on the flag before they reach the value. They are here because the row type
 * requires them, not because a group has a reorder level of nothing.
 */
function levelFromGroupFigures(
  group: StockLevelListItem,
  figures: StockLevelGroupFigures,
): StockLevelListItem {
  return {
    ...group,

    // Says the row is a TOTAL, so the columns a group cannot own render an em dash.
    isGroupTotal: true,

    // The figures are IN, so nothing below this row skeletons any more.
    figuresPending: false,

    investment: figures.investment,
    registeredUnits: figures.registeredUnits,
    onHand: figures.onHand,
    available: figures.available,
    unusable: figures.unusable,
    writtenOff: figures.writtenOff,

    expired: figures.expired,
    damaged: figures.damaged,
    underRepair: figures.underRepair,
    quarantined: figures.quarantined,

    condemned: figures.condemned,
    stolen: figures.stolen,

    onLoan: figures.onLoan,
    assigned: figures.assigned,
    delayedReturn: figures.delayedReturn,
    dispensed: figures.dispensed,
    forInspection: figures.forInspection,

    nearExpiry: figures.nearExpiry,

    leaseExpiring: figures.leaseExpiring,
    leaseExpired: figures.leaseExpired,
    earliestLeaseEnd: figures.earliestLeaseEnd,

    registeredProperties: figures.registeredProperties,
    leased: figures.leased,

    // The WORST among the children, derived by the server for the reason the browser used to
    // derive it: a group holding one out-of-stock item is a group with a shortage in it, and
    // the dot is the column a reader scans down.
    stockStatus: figures.stockStatus,

    latestMovementAt: figures.latestMovementAt,
  };
}

/**
 * Columns a PAGE TOTAL cannot own, however many groups it holds.
 *
 * <b>It is the group row's own rule read one level out</b> — total it or refuse it. A group
 * refuses the thresholds because two reorder levels added together reorder against nothing; the
 * page refuses those too, for free, because the summed row carries `isGroupTotal`. What it
 * refuses on its own account is:
 *
 * - <b>the STATUS</b>, in both of its columns. A group's is the WORST among its children, which
 *   is a real answer about one governed name; across every type on the page it is the worst
 *   thing anywhere, which says only that something somewhere is short.
 * - <b>the ANCHOR</b>, which carries the footer's own label instead.
 *
 * `earliestLeaseEnd` and `latestMovementAt` are deliberately NOT here: a page genuinely owns
 * the soonest lease end and the most recent movement, so they are a MIN and a MAX rather than a
 * sum. Refusing them would have been easier and would have thrown away two figures it can state.
 */
const FOOTER_REFUSES: ReadonlySet<string> = new Set(['name', 'stockDot', 'stockStatus']);

/**
 * The page's own row — every group's figures added up, in the shape the columns render.
 *
 * <b>It reuses the COLUMNS rather than formatting anything itself</b>, which is what keeps the
 * footer and the rows above it from disagreeing about a currency, a muted zero or an alignment.
 *
 * <b>It sums what has ARRIVED</b>, so it fills in behind the groups rather than waiting for the
 * slowest of them — and a group the filters emptied (a 404, held as `null`) contributes nothing
 * rather than a row of zeros, which is the distinction the group row itself already draws.
 */
function pageTotalRow(
  groups: StockLevelListItem[],
  figuresByType: Record<string, StockLevelGroupFigures | null>,
): StockLevelListItem | null {
  const present = groups
    .map((g) => figuresByType[g.inventoryItemTypeId])
    .filter((f): f is StockLevelGroupFigures => f != null);

  if (present.length === 0 || groups.length === 0) return null;

  const sum = (pick: (f: StockLevelGroupFigures) => number) =>
    present.reduce((total, f) => total + pick(f), 0);

  // Only the PRICED groups contribute, so a page whose stock arrived unpriced reads an em dash
  // rather than a total that quietly omits it — the investment column's own rule.
  const priced = present.filter((f) => f.investment != null);

  const leaseEnds = present.map((f) => f.earliestLeaseEnd).filter((d): d is string => d != null);
  const movements = present.map((f) => f.latestMovementAt).filter((d): d is string => d != null);

  return {
    ...groups[0],
    id: 'page-total',
    isGroupTotal: true,
    figuresPending: false,

    investment: priced.length === 0 ? null : priced.reduce((t, f) => t + (f.investment ?? 0), 0),
    registeredUnits: sum((f) => f.registeredUnits),
    onHand: sum((f) => f.onHand),
    available: sum((f) => f.available),
    unusable: sum((f) => f.unusable),
    writtenOff: sum((f) => f.writtenOff),

    expired: sum((f) => f.expired),
    damaged: sum((f) => f.damaged),
    underRepair: sum((f) => f.underRepair),
    quarantined: sum((f) => f.quarantined),

    condemned: sum((f) => f.condemned),
    stolen: sum((f) => f.stolen),

    onLoan: sum((f) => f.onLoan),
    assigned: sum((f) => f.assigned),
    delayedReturn: sum((f) => f.delayedReturn),
    dispensed: sum((f) => f.dispensed),
    forInspection: sum((f) => f.forInspection),

    nearExpiry: sum((f) => f.nearExpiry),

    leaseExpiring: sum((f) => f.leaseExpiring),
    leaseExpired: sum((f) => f.leaseExpired),

    // The SOONEST across the page — a minimum, which is the one way a group totals this column
    // and the one way the page can.
    earliestLeaseEnd: leaseEnds.length === 0 ? null : leaseEnds.reduce((a, b) => (a < b ? a : b)),

    registeredProperties: sum((f) => f.registeredProperties),
    leased: sum((f) => f.leased),

    // The MOST RECENT across the page — a maximum, for the reason the lease end is a minimum.
    latestMovementAt: movements.length === 0 ? null : movements.reduce((a, b) => (a > b ? a : b)),
  };
}

/**
 * ONE item type — its own row, its own figures fetch, and its own children fetch.
 *
 * <b>It is a component because each group loads INDEPENDENTLY</b>, and a hook cannot live in a
 * loop. That independence is the whole point of the split: a slow group fills in on its own
 * rather than holding every other row behind it, which is what the reader asked for in so many
 * words — <i>each row should have their own loading process, do not wait the whole item types
 * to be loaded.</i>
 */
function GroupRow({
  module,
  columns,
  group,
  cellClass,
  cellStyle,
  staleBar,
  queryKey,
  expandGroups,
  onFigures,
  reloadKey,
  refreshingTypeIds,
  fetchFigures,
  fetchChildren,
  fetchAging,
  agingColumns,
  rowKey,
  onRowOpen,
  rowActions,
  otherInfo,
  groupActions,
}: {
  module: string;
  columns: ColumnDef<StockLevelListItem>[];
  group: StockLevelListItem;
  cellClass: (column: ColumnDef<StockLevelListItem>) => string;
  cellStyle: (column: ColumnDef<StockLevelListItem>) => { left: number | undefined };
  staleBar: ReactNode;
  queryKey: string;
  /** Open on arrival — see the table's own prop. */
  expandGroups: boolean;
  /**
   * Hands this group's figures to the table, for the page TOTAL in the footer.
   *
   * <b>The figures live down here because each group fetches its own</b>, so the footer cannot
   * read them without being told. It is reported as it LANDS — including the `null` a 404
   * gives — so the total fills in behind the rows rather than waiting for the slowest group.
   */
  onFigures: (typeId: string, figures: StockLevelGroupFigures | null) => void;
  reloadKey: number;
  refreshingTypeIds: readonly string[] | null;
  fetchFigures: (typeId: string, signal: AbortSignal) => Promise<StockLevelGroupFigures | null>;
  fetchChildren: (typeId: string, signal: AbortSignal) => Promise<StockLevelListItem[]>;

  /** The fifth call — the figures that age, fetched apart so the rows appear first. */
  fetchAging: (typeId: string, signal: AbortSignal) => Promise<StockLevelAging[]>;

  /** The columns whose cells draw `staleBar` until that call lands. See `AGING_COLUMN_KEYS`. */
  agingColumns: ReadonlySet<string>;
  rowKey: (row: StockLevelListItem) => string;
  onRowOpen?: (row: StockLevelListItem, agingKnown: boolean) => void;
  rowActions?: (row: StockLevelListItem, agingKnown: boolean) => MenuItem[];
  otherInfo: (row: StockLevelListItem) => OtherInfoData | null;
  groupActions?: (group: { id: string; name: string; items: StockLevelListItem[] }) => MenuItem[];
}) {
  // The GROUP's id — an item type, or a category on the Real Property tab. See
  // `pendingGroupRow`, which is what put it in this field.
  const typeId = group.inventoryItemTypeId;

  /*
    COLLAPSED by default, which is the hospital’s call and the right one: the reader opens this
    page to find what to reorder, and that question is answered by the summed row. Expanding is
    how they ask the second question — which SKU of this name is short.

    It is the ROW’s own state now rather than a set in the table, because a group already owns
    two other pieces of state that have to be dropped together with it.
  */
  const [open, setOpen] = useState(expandGroups);

  /*
    AND IT RE-DERIVES WHEN THE SEARCH STARTS OR STOPS, without an effect.

    This is the React pattern for adjusting state to a prop: compare against the value last
    rendered with, and set during render. An effect would open the group one PAINT late — the
    reader sees the search land on a shut folder and watches it open afterwards, which reads as
    the page correcting itself. Deriving `open` outright is not an option either: the reader can
    toggle a group by hand, and that choice has to survive the next render.

    It is SYMMETRIC on purpose. Clearing the search puts every group back the way the page
    opens, rather than leaving a screen of expanded rows behind as a side effect of a search the
    reader has finished with.
  */
  const [lastExpandGroups, setLastExpandGroups] = useState(expandGroups);
  if (expandGroups !== lastExpandGroups) {
    setLastExpandGroups(expandGroups);
    setOpen(expandGroups);
  }

  /*
    THREE STATES, and the middle one is the reason this is not a boolean.

      undefined  in flight — the row skeletons
      null       the server answered 404: nothing here matches the filters
      figures    the totals

    A 404 renders an em dash rather than a row of zeros, which is the same distinction the
    investment column already draws: zero would claim the type holds nothing, where the honest
    answer is that nothing here matches what the reader has narrowed to.
  */
  const [figures, setFigures] = useState<StockLevelGroupFigures | null | undefined>(undefined);

  /** The children, once the reader has expanded. `undefined` until then — never fetched. */
  const [children, setChildren] = useState<StockLevelListItem[] | undefined>(undefined);

  /** The aging figures for this group, keyed by item. `undefined` while the call is in flight. */
  const [aging, setAging] = useState<Map<string, StockLevelAging> | undefined>(undefined);

  /*
    A WRITE this group is the subject of. Product call — the reader’s own words: <i>only that
    item group should reload with skeleton loader … do not reload the whole tab/page.</i>

    The page names the TYPES a write moved, so every other group is left exactly as it is — its
    figures, its children, its scroll and its open state all survive. A bare refresh names none
    and every group reloads, which is the honest treatment for a write whose reach the caller
    cannot state.
  */
  const applied = useRef(reloadKey);

  if (refreshingTypeIds === null || refreshingTypeIds.includes(typeId)) applied.current = reloadKey;

  /*
    The reload counter AS THIS GROUP HAS SEEN IT. A write naming another type leaves it where
    it was, so nothing below re-runs and this row keeps its figures, its children and its open
    state while the group the reader acted on reloads.

    Advanced during render rather than in an effect: the effects below have to be able to READ
    it in the same pass they are scheduled from, and an effect would set it one render late —
    which is one render in which the subject group has already refetched and this one is about
    to. It is idempotent, so a double render decides the same thing twice.
  */
  const writeKey = applied.current;

  /*
    THE FIGURES. Refetched when the query changes, and when a write this group owns lands.

    `queryKey` carries the page’s reload counter as well as the filters, so a save moves it and
    a group that is not the subject of that save pins `writeKey` to a constant and never sees
    the change — which is what scopes the skeleton to one group rather than blanking the table.
  */
  useEffect(() => {
    const controller = new AbortController();

    setFigures(undefined);

    fetchFigures(typeId, controller.signal)
      .then((answer) => {
        if (controller.signal.aborted) return;
        setFigures(answer);
        onFigures(typeId, answer);
      })
      .catch(() => {
        // A FAILED fetch reads as "no figures", not as an exception the table has to render.
        // The row keeps its name, its count and its disclosure, which is what the reader needs
        // to try again — and an abort is the ordinary case here rather than a failure.
        if (controller.signal.aborted) return;
        setFigures(null);
        onFigures(typeId, null);
      });

    return () => controller.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [typeId, queryKey, writeKey, fetchFigures, onFigures]);

  /*
    THE CHILDREN ARE DROPPED when the question changes, and re-fetched only if the group is
    still open. Keeping them would leave a reader looking at rows that answer the previous
    filter under a total that answers this one.
  */
  useEffect(() => {
    setChildren(undefined);
    setAging(undefined);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [typeId, queryKey, writeKey]);

  /*
    THE AGING FIGURES ARRIVE ON THEIR OWN CALL, and the rows do not wait for them.

    `undefined` means "still coming" and an empty map means "came back with nothing" — the two
    have to be distinguishable, because a cell with no answer yet renders a SKELETON while one
    with a real zero renders a muted zero, and a muted zero is exactly what "none expired" looks
    like. Collapsing them would put a wrong answer on screen rather than an absent one.
  */
  useEffect(() => {
    if (!open || aging !== undefined) return;

    const controller = new AbortController();

    fetchAging(typeId, controller.signal)
      .then((rows) => {
        if (!controller.signal.aborted) {
          setAging(new Map(rows.map((row) => [row.id, row])));
        }
      })
      .catch(() => {
        if (!controller.signal.aborted) setAging(new Map());
      });

    return () => controller.abort();
  }, [open, aging, typeId, fetchAging]);

  /*
    ON EXPAND, and never before — which is the third progressive call and the one the reader
    asked for by name: <i>do not load the item child info, only load them when expanded.</i>

    It is also the only call that runs the projection at all, so a page of ten groups nobody
    opens runs it zero times.
  */
  useEffect(() => {
    if (!open || children !== undefined) return;

    const controller = new AbortController();

    fetchChildren(typeId, controller.signal)
      .then((answer) => {
        if (!controller.signal.aborted) setChildren(answer);
      })
      .catch(() => {
        if (!controller.signal.aborted) setChildren([]);
      });

    return () => controller.abort();
  }, [open, children, typeId, fetchChildren]);

  /** How many rows expanding will show — the server’s count, so it is right before they load. */
  const itemCount = group.groupItemCount ?? children?.length ?? 0;

  /** The row the columns actually render. Only ever built from figures that have ARRIVED. */
  const total = figures ? levelFromGroupFigures(group, figures) : null;

  return (
    <Fragment>
      {/*
        THE ORDINARY ROW GROUND — white at rest, slate-50 on hover, exactly as the child rows
        below it. Product call, 23 Sep 2026.

        It was tinted `bg-slate-50` at rest, which made the group the only row in the product
        with a ground of its own: read down a collapsed table every row was grey, and a reader
        meets a table that looks disabled rather than one with headings in it. What says a group
        is a group is what says it while it is SHUT — the disclosure, the count and the kebab —
        and none of those is the background.

        OPAQUE, and that half is unchanged whatever the ground is: a frozen cell has to carry the
        row’s own colour or the columns it is pinned over scroll through it.
      */}
      <tr className="group border-t border-slate-200 bg-white align-middle hover:bg-slate-50">
        {columns.map((column, index) => (
          <td key={column.key} className={cellClass(column)} style={cellStyle(column)}>
            {index === 0 ? (
              <div className="flex items-center gap-2">
                {/*
                  The disclosure states what is RENDERED — pointing down when the group is open
                  — which is the product’s one chevron rule.
                */}
                <button
                  type="button"
                  onClick={() => setOpen((current) => !current)}
                  aria-expanded={open}
                  aria-label={`${open ? 'Collapse' : 'Expand'} ${group.name}`}
                  className="rounded p-0.5 text-slate-500 transition hover:bg-slate-200 hover:text-slate-700"
                >
                  <ChevronRight
                    className={`h-3.5 w-3.5 transition-transform ${open ? 'rotate-90' : ''}`}
                    aria-hidden="true"
                  />
                </button>

                {/*
                  Takes the slack, so the count and the kebab sit at the column’s OUTER RIGHT
                  rather than trailing the name — a column of counts is read down its own edge.

                  `slate-600`, a step back from the child rows rather than ahead of them. A group
                  row is CHROME naming a run of records, not one of the records: the anchors
                  below it are what a reader clicks, and at full strength the heading competed
                  with them for exactly the attention they need.

                  THE WEIGHT IS GONE TOO — product call, 28 Sep 2026. Two signals remain and both
                  are structural rather than typographic: the DISCLOSURE chevron, which no child
                  row has, and the COUNT beside the name.
                */}
                <span className="min-w-0 flex-1 truncate text-slate-600" title={group.name}>
                  {group.name}
                </span>

                {/*
                  The count is what says the group is a group even while it is shut, and it is
                  the reader’s only cue that expanding will show anything. It is the SERVER’s,
                  so it is right before a single child has been fetched.
                */}
                <span className="shrink-0 text-[11px] text-slate-400">
                  {itemCount} {itemCount === 1 ? 'item' : 'items'}
                </span>

                {/*
                  The TYPE’s own menu, and it is `<RowActionMenu>` like every other row menu in
                  the product — so it right-clicks, filters by permission and orders its entries
                  identically. `otherInfo` is null because a group is not a record: it is a run of
                  rows sharing a catalog entry, with no creator and no trail of its own.

                  <b>`items` is what the group has LOADED</b>, which is empty until the reader
                  expands. Nothing in the menu reads it today — both entries address the TYPE —
                  and an entry that needed the children would have to say so rather than quietly
                  acting on none of them.
                */}
                {groupActions ? (
                  <RowActionMenu
                    id={`${module}-group-${typeId}`}
                    otherInfo={null}
                    extraItems={groupActions({
                      id: typeId,
                      name: group.name,
                      items: children ?? [],
                    })}
                  />
                ) : null}
              </div>
            ) : total === null ? (
              /*
                THE FIGURES ARE NOT IN. A skeleton per cell, and the NAME, the count and the
                kebab stay: the reader has to be able to see which group is still loading, and
                to expand or collapse it while it does.

                `figures === null` — a 404, or a failed fetch — lands here too, and that is a
                deliberate simplification rather than a missing branch: a group the filters
                emptied has no row in the listing above it, so the state is unreachable from the
                shell’s own query. If one ever is reachable it owes an em dash, not a bar.
              */
              staleBar
            ) : (
              column.render(total, 0)
            )}
          </td>
        ))}
      </tr>

      {open && children === undefined ? (
        /*
          THE CHILDREN ARE COMING. One placeholder row per item the server said is in there, so
          the table does not resettle when they land — a skeleton is the shape of what it
          replaces, and here the server has already told us that shape exactly.
        */
        <SkeletonRows
          rows={Math.max(1, Math.min(itemCount, 10))}
          columns={columns.length}
          cell={(index) => {
            const column = columns[index]!;
            return { className: cellClass(column), style: cellStyle(column) };
          }}
        />
      ) : null}

      {open && children !== undefined && children.length === 0 ? (
        <tr className="border-t border-slate-200 bg-white">
          <td colSpan={columns.length} className="px-3 py-4 text-center text-xs text-slate-500">
            Nothing under this type matches the current filters.
          </td>
        </tr>
      ) : null}

      {/*
        THE CAP SAYS SO. A group’s children arrive in ONE page with no pager under them — the
        shell’s pager moved to the GROUPS — so a type holding more than the platform’s maximum
        page would otherwise stop short silently, which is exactly what a pager exists to stop.

        It names the two numbers and where the rest are, because the reader can act on that:
        Stock Batches and Stock Units are paged lists over the same rows.
      */}
      {open && children !== undefined && children.length > 0 && children.length < itemCount ? (
        <tr className="border-t border-slate-200 bg-white">
          <td colSpan={columns.length} className="px-3 py-2 text-center text-[11px] text-slate-500">
            Showing {children.length} of {itemCount} — narrow the search, or open Stock Batches
            or Stock Units for the full list.
          </td>
        </tr>
      ) : null}

      {open
        ? (children ?? []).map((stored, position) => {
            /*
              THE ROW THE COLUMNS RENDER — the stored figures with the aging ones merged over
              them, which is the only place the two halves meet.

              While the aging call is in flight the row carries whatever `Project` left in those
              fields, and the cells below render a SKELETON rather than those values. The merge
              is done here rather than in state so a re-fetch cannot leave a row half-updated.
            */
            const figuresFor = aging?.get(stored.id);
            const row = figuresFor ? { ...stored, ...withoutId(figuresFor) } : stored;

            /*
              WHETHER THE TEN AGING FIGURES ON THIS ROW ARE REAL — the CALL having landed, not
              this item having been in the answer.

              It is deliberately the same test the skeleton below makes, because the two surfaces
              have to agree: the table treats "call landed, item absent" as values-are-real, so a
              panel opened from it must too. A reader meeting a skeleton here and a zero there
              would be looking at one item described two ways.
            */
            const agingKnown = aging !== undefined;

            return (
            <tr
              key={rowKey(row)}
              // `bg-white` explicitly, because a frozen cell inherits no ground from the table
              // and needs one of its own to pin over.
              className="group border-t border-slate-200 bg-white align-middle hover:bg-slate-50"
            >
              {columns.map((column, index) => (
                <td key={column.key} className={cellClass(column)} style={cellStyle(column)}>
                  {index === 0 ? (
                    <div className="flex items-center gap-2">
                      {/* Indented past the group’s own disclosure, so a child reads as sitting
                          under its parent rather than beside it. */}
                      <span className="w-4 shrink-0" aria-hidden="true" />

                      <div className="min-w-0 flex-1">
                        {/*
                          The anchor opens the record, and it is a BUTTON inside the cell rather
                          than a handler on the row — a row is not a control, and making one
                          swallows every click on the menu beside it. `AnchorCellContext` is what
                          paints it in the product’s anchor blue; hard-coding the tone here is the
                          drift that context exists to prevent.
                        */}
                        <AnchorCellContext.Provider value>
                          {onRowOpen ? (
                            <button
                              type="button"
                              onClick={() => onRowOpen(row, agingKnown)}
                              className="block w-full min-w-0 cursor-pointer rounded text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-300"
                            >
                              {column.render(row, position)}
                            </button>
                          ) : (
                            column.render(row, position)
                          )}
                        </AnchorCellContext.Provider>
                      </div>

                      {/*
                        The row menu sits INSIDE the anchor column, not in a pinned trailing
                        gutter — this table is thirty columns wide and a gutter would be a
                        horizontal scroll away from the row it acts on. It is still
                        `<RowActionMenu>`, so the right-click, the permission filtering and Other
                        Info all arrive unchanged.
                      */}
                      <RowActionMenu
                        id={`${module}-actions-${rowKey(row)}`}
                        otherInfo={otherInfo(row)}
                        extraItems={rowActions?.(row, agingKnown)}
                      />
                    </div>
                  ) : aging === undefined && agingColumns.has(column.key) ? (
                    /*
                      THE AGING FIGURES ARE NOT IN, and this cell is one of theirs.

                      The row below it is complete — every STORED figure arrived with the
                      children — so the skeleton is per column rather than per row, which is the
                      whole shape of the split: the rows appear first and the clock-driven
                      figures fill in behind them.

                      It must be a SKELETON and not the value: `Project` leaves these fields at
                      zero and `WithAging` is what fills them, so rendering `row` here would
                      print a muted zero — which is exactly what "none expired" looks like, and
                      a wrong answer a reader cannot tell from a right one.
                    */
                    staleBar
                  ) : (
                    column.render(row, position)
                  )}
                </td>
              ))}
            </tr>
            );
          })
        : null}
    </Fragment>
  );
}